// @ts-nocheck
import React, {
  useCallback,
  useMemo,
  useState,
  useEffect,
  useRef,
} from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Grid,
  TextField,
  Tooltip,
} from '@mui/material';
import PropTypes from 'prop-types';
import { ReactComponent as CloseIcon } from '../../../assets/Icons/lucideIcons/x.svg';
import { useFormik } from 'formik';
import { object, string } from 'yup';
import { ReactComponent as InfoOutlinedIcon } from '../../../assets/Icons/lucideIcons/info.svg';

import Overview from '../../Common/AttributesForm/Overview';
import useEntityDetailsForm from '../../../hooks/useEntityDetailsForm';
import useSegments from '../../../hooks/useSegments';
import useAttribute from '../../../hooks/ModelInventory/useAttribute';
import { DropDown, DropdownItem } from '../../Common/DropDown';
import {
  EMPTY_ARRAY,
  INCLUDE_ATTRIBUTES_NAME_FORM_VALUES_DURING_SEGMENT_CREATION,
  INCLUDE_DATA_TYPE_FORM_VALUES_DURING_SEGMENT_CREATION,
  EXCLUDE_DATA_TYPE_DURING_SEGMENT_CREATION_AND_COPYING,
  SEGMENT_DATA,
  DEFAULT_SEGMENT_ID,
} from '../../../const/CommonConst';
import {
  extractAllAttributesFromTemplate,
  getAutoFillAttributes,
} from '../../../features/configuration/template/components/SectionAndAttribute/commonFunction';
import ConfirmModal from '../../../components/Common/Modals/ConfirmModal';
import SwalToast from '../../Common/swalToast';

const handleOpenNoteModal = () => {};

const CreateEditSegmentModal = ({
  open,
  handleClose,
  mode = 'create', // 'create', 'edit', 'copy', 'view', 'replace'
  title,
  segment,
  formDetails,
  attributeDetails,
  handleUpdateEntity: _handleUpdateEntity,
  allUserList,
  teamUsersList,
  currentModel,
  onSuccess,
  deleteNote,
  editNote,
  handleSetAddNoteModalHeading,
  handleSetSelectedEntityToAddInEditor,
  getAttComments,
  allAttributeChats,
  breadcrumbDetails,
  isUserAuthorizedForUpdateAttribute,
  getOverrideAttributeValue,
  segmentsData = EMPTY_ARRAY,
  allTemplateData,
  isSegmentAttribute = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [showValidation, setShowValidation] = useState(false);
  const [matchedSegmentIds, setMatchedSegmentIds] = useState([]);
  const [selectedSegmentToCopy, setSelectedSegmentToCopy] =
    useState(DEFAULT_SEGMENT_ID);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  // Used to skip the "no changes" check in copy mode after the user clicks "Yes".
  // We use a ref so the save function runs immediately without reopening the confirmation modal.
  const bypassCopyDirtyCheckRef = useRef(false);
  // Ensures update-mode prefill runs only once per modal open.
  // Tracks whether the modal has set its initial dropdown values
  const initializationRef = useRef(false);
  // Tracks the segment_id of the most recent getAttributeDetails request so that
  // out-of-order responses from rapid segment switching are discarded.
  const latestSegmentRequestRef = useRef(null);
  // Use the segments hook
  const { createSegment, updateSegment } = useSegments();
  const { getAttributeDetails } = useAttribute();
  const [fetchedUpdateSegmentData, setFetchedUpdateSegmentData] =
    useState(null);
  const overviewRef = useRef();

  // Form validation schema
  const validationSchema = useMemo(() => {
    return object({
      segmentName: string()
        .required('Segment name is required')
        .max(200, 'Segment name must be 200 characters or less'),
      description: string().max(
        500,
        'Description must be 500 characters or less'
      ),
      justification: string()
        .max(500, 'Justification must be 500 characters or less')
        .test(
          'required-if-attribute',
          'Justification is required',
          function (value) {
            if (isSegmentAttribute) {
              return value;
            }
            return true;
          }
        ),
      selectedSegmentToCopy:
        mode === 'copy' || mode === 'replace' || mode === 'update'
          ? string().required('Please select a segment')
          : string(),
    });
  }, [mode, isSegmentAttribute]);

  /**
   * Identifies any existing segments that have identical attribute values to the current form state.
   * This prevents the creation of duplicate segments by comparing normalized attribute data.
   *
   * @param {Object} currentSegmentAttributes - The current state of attributes from the create or copy segment form (key-value pairs).
   * @param {Array} existingSegmentsList - List of all existing segments to compare against.
   * @param {Object} defaultAttributeDetails - Default attribute details from the model.
   * @param {Object} templateFormData - The form template structure containing section and attribute definitions.
   * @returns {Array<string>} An array of segment IDs that match the current attribute state.
   */
  const getMatchingSegmentIds = (
    currentSegmentAttributes = {},
    existingSegmentsList = [],
    defaultAttributeDetails = {},
    templateFormData = {},
    excludedsegmentAttributeIds = []
  ) => {
    // Combine the default segment with the list of existing segments
    const allSegmentsToCompare = [
      {
        segment_id: DEFAULT_SEGMENT_ID,
        attribute_data: defaultAttributeDetails || {},
      },
      ...existingSegmentsList,
    ];

    // Extract all attribute from the template
    const allTemplateAttributes =
      extractAllAttributesFromTemplate(templateFormData) || [];

    // Filter and extract valid attribute IDs for comparison
    // We exclude certain input control types (like Advanced Table, Document) based on the configuration
    const validAttributeKeys = allTemplateAttributes
      ?.filter(
        (attribute) =>
          // ensuring that attribute is present
          attribute &&
          attribute?.is_derived !== true &&
          !EXCLUDE_DATA_TYPE_DURING_SEGMENT_CREATION_AND_COPYING?.includes(
            attribute?.data_type
          ) &&
          !excludedsegmentAttributeIds?.includes(attribute?.id)
      )
      ?.map((attribute) => attribute?.id)
      ?.sort((a, b) => a - b); // Sort keys to ensure consistent order for stringification

    // Helper to normalize attribute values (treating undefined, null, and empty string as null)
    const normalizeAttributeValue = (value) => {
      if (value === undefined || value === null || value === '') return null;
      return value;
    };

    // Helper to create a normalized object containing only the valid keys
    const createNormalizedAttributeObject = (sourceAttributes) => {
      const normalizedObject = {};
      validAttributeKeys?.forEach((key) => {
        normalizedObject[key] = normalizeAttributeValue(sourceAttributes[key]);
      });
      return normalizedObject;
    };

    // Create a stringified representation of the current segment attributes
    const currentAttributesStringified = JSON.stringify(
      createNormalizedAttributeObject(currentSegmentAttributes)
    );

    // Filter segments that have a matching stringified attribute representation
    return allSegmentsToCompare
      ?.filter((seg) => {
        const segmentAttributesStringified = JSON.stringify(
          createNormalizedAttributeObject(seg.attribute_data || {})
        );
        return segmentAttributesStringified === currentAttributesStringified;
      })
      ?.map((seg) => seg?.segment_id);
  };

  // Get selected segment attribute data for copy mode
  const selectedSegmentAttributeData = useMemo(() => {
    if (
      (mode === 'copy' || mode === 'replace' || mode === 'update') &&
      selectedSegmentToCopy
    ) {
      if (selectedSegmentToCopy === DEFAULT_SEGMENT_ID) {
        return attributeDetails || {};
      }
      if (mode === 'update') {
        return fetchedUpdateSegmentData?.attribute_data || {};
      }
      const foundSegment = segmentsData?.find(
        (seg) => seg?.segment_id === selectedSegmentToCopy
      );
      return foundSegment?.attribute_data || {};
    }
    return {};
  }, [
    mode,
    selectedSegmentToCopy,
    attributeDetails,
    segmentsData,
    fetchedUpdateSegmentData,
  ]);
  /**
   * extraction of all attributes from the provided template.
   *
   * This prevents repeated traversal of the template structure
   * (sections → attributes) on every render.
   *
   * Recomputes only when `formDetails` changes.
   *
   * @type {Array<Object>}
   */
  const allTemplateAttributes = useMemo(() => {
    return extractAllAttributesFromTemplate(formDetails) || [];
  }, [formDetails]);

  /**
   * Derives the attribute IDs for Segment-specific fields (B1 & B2)
   * based on their configured attribute names in SEGMENT_DATA.
   *
   * - SEGMENT_NUMBER → B1 (auto-incremented segment number)
   * - SEGMENT_NAME   → B2 (synced with segmentName input)
   * Recomputes only when `allTemplateAttributes` changes.
   *
   * @returns {{
   *   b1AttributeId: string | number | undefined,
   *   b2AttributeId: string | number | undefined
   * }}
   */
  const { b1AttributeId, b2AttributeId } = useMemo(() => {
    let b1Id;
    let b2Id;
    for (const attr of allTemplateAttributes) {
      if (attr?.name === SEGMENT_DATA.SEGMENT_NUMBER) {
        b1Id = attr?.id;
      }
      if (attr?.name === SEGMENT_DATA.SEGMENT_NAME) {
        b2Id = attr?.id;
      }
    }
    return {
      b1AttributeId: b1Id,
      b2AttributeId: b2Id,
    };
  }, [allTemplateAttributes]);
  // for fetching attribute IDs to fill initial form values
  const autoFillAttributeIds = useMemo(() => {
    // Get filtered attribute IDs based on data_type and name
    const filteredAttributeIds = allTemplateAttributes
      .filter(
        (attr) =>
          INCLUDE_DATA_TYPE_FORM_VALUES_DURING_SEGMENT_CREATION?.includes(
            attr?.data_type
          ) ||
          INCLUDE_ATTRIBUTES_NAME_FORM_VALUES_DURING_SEGMENT_CREATION?.includes(
            attr?.name
          )
      )
      .map((attr) => attr?.id);

    // Get visible section IDs from formDetails (already filtered by permissions)
    const visibleSectionIds = new Set(
      formDetails?.sections?.map((section) => section?.section_id) || []
    );

    // Get attributes from hidden sections (sections in allTemplateData but not in formDetails)
    const nonVisibleAttributeIds = [];
    if (allTemplateData?.sections && Array.isArray(allTemplateData?.sections)) {
      allTemplateData?.sections?.forEach((section) => {
        // If section is not in visible sections, it's a hidden section
        if (!visibleSectionIds.has(section?.section_id)) {
          // Extract all attribute IDs from this hidden section
          if (section?.attributes && Array.isArray(section?.attributes)) {
            section?.attributes?.forEach((attr) => {
              if (attr?.id) {
                nonVisibleAttributeIds.push(attr?.id);
              }
            });
          }
        }
      });
    }

    // Combine filtered attributes and non-visible attributes (from hidden sections)
    return [...new Set([...filteredAttributeIds, ...nonVisibleAttributeIds])];
  }, [allTemplateAttributes, formDetails, allTemplateData]);

  const updatableSegments = useMemo(
    () =>
      segmentsData?.filter(
        (seg) =>
          seg?.segment_id !== DEFAULT_SEGMENT_ID && !seg?.is_decomissioned
      ) ?? [],
    [segmentsData]
  );

  const nextSegmentNumber = useMemo(() => {
    if (!segmentsData?.length) {
      return '02';
    }
    const numericIds = segmentsData
      .map((seg) =>
        Number(seg?.segment_id || seg?.attribute_data?.[b1AttributeId])
      )
      .filter((id) => !Number.isNaN(id));
    const maxId = numericIds?.length ? Math?.max(...numericIds) : 0;
    return String(maxId + 1).padStart(2, '0');
  }, [segmentsData, b1AttributeId]);

  // Prepare attribute details for the hook
  const isCreateOrCopy = mode === 'create' || mode === 'copy';

  const modalAttributeDetails = useMemo(() => {
    if (mode === 'edit' || mode === 'view') {
      return segment?.attribute_data || {};
    }
    // create / copy
    const baseAttributeData = selectedSegmentAttributeData || {};
    const autoFilledAttributes = getAutoFillAttributes({
      autoFillAttributeIds,
      baseAttributeData,
      attributeDetails,
    });

    return {
      ...baseAttributeData,
      ...autoFilledAttributes,
      ...(isCreateOrCopy &&
        b1AttributeId && {
          [b1AttributeId]: nextSegmentNumber,
        }),
    };
  }, [
    mode,
    segment,
    selectedSegmentAttributeData,
    autoFillAttributeIds,
    attributeDetails,
    b1AttributeId,
    nextSegmentNumber,
  ]);
  //Filtering these attributes during the identical segment check, as they are dynamically updated based on the segment being created.
  const excludedSegmentAttributeIds = useMemo(() => {
    return [b1AttributeId, b2AttributeId].filter(Boolean);
  }, [b1AttributeId, b2AttributeId]);
  // Memoized empty sync attributes to prevent unnecessary re-renders
  const syncAttributes = useMemo(() => ({}), []);

  // Memoized update callback to prevent unnecessary re-renders
  const updateEntityCallback = useCallback(async (updatedEntityData) => {
    // This will be called when attributes are updated
    // We'll handle the actual save in our custom save handler
    return Promise.resolve(updatedEntityData);
  }, []);

  // Memoized success callback to prevent unnecessary re-renders
  const onSuccessCallback = useCallback(() => {
    // Handle success in our custom save handler
  }, []);

  // Use the entity details form hook
  const {
    entityDetailsFormikForm,
    handleEditForm,
    handleCancelEditForm,
    allAttributes,
    validateFilteredFormikErrors,
    setFormSchema,
  } = useEntityDetailsForm({
    formDetails,
    attributeDetails: modalAttributeDetails,
    entityId: currentModel?.entity_id,
    entityType: currentModel?.entity_type,
    updateEntityCallback,
    onSuccess: onSuccessCallback,
    successMessage: 'Segment saved successfully.',
    syncAttributes,
  });

  // Memoized initial values for Formik
  const initialFormValues = useMemo(() => {
    if (
      segment &&
      (mode === 'edit' ||
        mode === 'copy' ||
        mode === 'view' ||
        mode === 'replace')
    ) {
      return {
        segmentName: segment.segment_name || '',
        description: segment.description || '',
        justification: segment.justification || '',
        selectedSegmentToCopy: DEFAULT_SEGMENT_ID,
      };
    }
    return {
      segmentName: '',
      description: '',
      justification: '',
      selectedSegmentToCopy: DEFAULT_SEGMENT_ID,
    };
  }, [segment, mode]);

  // Formik form for segment name and description
  const formikForm = useFormik({
    initialValues: initialFormValues,
    validationSchema,
    enableReinitialize: true, // This allows the form to reinitialize when initialValues change
    onSubmit: () => {
      // Handled by custom save handler
    },
  });

  const getInfoTitle = useCallback(() => {
    switch (mode) {
      case 'create':
        return 'Creates a new segment from scratch. No values are prefilled.';
      case 'copy':
        return ' Allows copying an existing segment. The selected segment’s configuration and values are prefilled into the form.';
      case 'replace':
        return 'Allows replacing an existing segment. The selected segment’s configuration is prefilled.';
      case 'update':
        return 'Queues changes to an existing segment. The changes will be applied when the workflow transitions.';
      default:
        return '';
    }
  }, [mode]);
  // Shared helper: prefill the three text fields + B2 attribute, then fetch and commit
  // attribute details for the given segment.
  const prefillAndFetchSegmentDetails = useCallback(
    (segmentData) => {
      formikForm.setFieldValue('segmentName', segmentData?.segment_name || '');
      formikForm.setFieldValue('description', segmentData?.description || '');
      formikForm.setFieldValue(
        'justification',
        segmentData?.justification || ''
      );
      if (b2AttributeId && entityDetailsFormikForm) {
        entityDetailsFormikForm.setFieldValue(
          b2AttributeId,
          segmentData?.segment_name || '',
          false // // Set to false to avoid validating against the initial empty schema before it has fully initialized
        );
      }
      const requestId = segmentData?.segment_id;
      setFetchedUpdateSegmentData(null);
      latestSegmentRequestRef.current = requestId;
      getAttributeDetails({
        entity_id: segmentData?.entity_id || currentModel?.entity_id,
        entity_type: currentModel?.entity_type,
      })
        .then((res) => {
          if (latestSegmentRequestRef.current !== requestId) return;
          setFetchedUpdateSegmentData({ attribute_data: res?.data ?? {} });
        })
        .catch(() => {
          if (latestSegmentRequestRef.current !== requestId) return;
          setFetchedUpdateSegmentData(null);
        });
    },
    [
      b2AttributeId,
      getAttributeDetails,
      currentModel?.entity_id,
      currentModel?.entity_type,
    ]
  );

  // Handle segment selection change
  const handleSegmentToCopyChange = useCallback(
    (event) => {
      const value = event.target.value;
      setSelectedSegmentToCopy(value);
      formikForm.setFieldValue('selectedSegmentToCopy', value);
      if (mode === 'update' && value && value !== DEFAULT_SEGMENT_ID) {
        const foundSegment = segmentsData?.find(
          (seg) => seg?.segment_id === value
        );
        if (foundSegment) {
          prefillAndFetchSegmentDetails(foundSegment);
        }
      }
    },
    [mode, segmentsData, prefillAndFetchSegmentDetails]
  );

  // Close handler
  const handleModalClose = useCallback(() => {
    latestSegmentRequestRef.current = null;
    formikForm.resetForm();
    setShowValidation(false);
    setSelectedSegmentToCopy(DEFAULT_SEGMENT_ID);
    setFetchedUpdateSegmentData(null);
    handleCancelEditForm();
    handleClose();
  }, [formikForm, handleCancelEditForm, handleClose]);

  const openConfirmModalCopySegment = useCallback(() => {
    setIsConfirmModalOpen(true);
  }, [setIsConfirmModalOpen]);

  const closeConfirmModalCopySegment = useCallback(() => {
    setIsConfirmModalOpen(false);
  }, [setIsConfirmModalOpen]);

  const handleConfirmClose = useCallback(() => {
    closeConfirmModalCopySegment();
  }, [closeConfirmModalCopySegment]);

  // Custom save handler that combines segment name and attributes
  const handleSaveSegment = useCallback(async () => {
    try {
      setLoading(true);
      setShowValidation(true);
      const isValid = await overviewRef?.current?.validateAndShowErrors?.();
      // If invalid, validateAndShowErrors will have opened the dialog and returned false
      if (!isValid) {
        return;
      }
      // Validate formik form
      const formErrors = await formikForm.validateForm();
      formikForm.setTouched({
        segmentName: true,
        description: true,
        justification: true,
        selectedSegmentToCopy: true,
      }); // Check if there are formik validation errors
      if (Object.keys(formErrors).length > 0) {
        setLoading(false);
        return;
      }

      // Trigger form validation and check if form is valid
      if (entityDetailsFormikForm) {
        // Trigger validation for all fields
        const errors = await validateFilteredFormikErrors();

        // Filter out false positive mandatory errors for Segments attributes (if isSegmentAttribute)
        if (isSegmentAttribute) {
          const allFields = extractAllAttributesFromTemplate(formDetails);
          const newDataTypeAttrs = allFields?.filter(
            (attr) => attr?.data_type?.toLowerCase() === 'segments'
          );

          newDataTypeAttrs?.forEach((attr) => {
            const isMandatory =
              Array.isArray(attr?.yup_schema) &&
              attr?.yup_schema?.some((rule) => rule?.type === 'required');
            if (!isMandatory && errors[attr.id]) {
              delete errors[attr.id];
            }
          });
        }

        const touchedFields = Object.keys(
          entityDetailsFormikForm.values
        ).reduce((acc, key) => {
          return { ...acc, [key]: true };
        }, {});
        entityDetailsFormikForm.setTouched(touchedFields);

        // Check if there are any validation errors
        if (Object.keys(errors).length > 0) {
          setLoading(false);
          return;
        }
      }

      // Get attribute values from the form
      const attributeValues = entityDetailsFormikForm?.values || {};

      // To show confirmation modal when no value changed during copy and create section
      if (
        (mode === 'create' || mode === 'copy' || mode === 'replace') &&
        entityDetailsFormikForm &&
        !bypassCopyDirtyCheckRef.current
      ) {
        const matchedIds = getMatchingSegmentIds(
          attributeValues,
          segmentsData,
          attributeDetails,
          formDetails,
          excludedSegmentAttributeIds
        );

        if (matchedIds?.length > 0) {
          setMatchedSegmentIds(matchedIds);
          openConfirmModalCopySegment();
          setLoading(false);
          return;
        }
      }

      if (isSegmentAttribute) {
        // New Data Type Logic: Local Update via onSuccess
        const payload = {
          segment_name: formikForm.values.segmentName.trim(),
          description: formikForm.values.description.trim(),
          justification: formikForm.values.justification.trim(),
          attribute_data: {
            ...attributeValues,
          },
        };

        if (mode === 'edit') {
          payload.segment_id = segment?.segment_id;
        } else if (mode === 'update') {
          payload.segment_id = selectedSegmentToCopy;
          payload.is_updated = true;
        } else if (mode === 'replace') {
          payload.replace_segment_id = selectedSegmentToCopy;
        }

        onSuccess(payload);
        handleModalClose();

        let toastTitle = 'Segment created locally';
        if (mode === 'edit') {
          toastTitle = 'Segment updated locally';
        } else if (mode === 'update') {
          toastTitle = 'Segment update staged for transition';
        }
        SwalToast({
          title: toastTitle,
          icon: 'success',
        });
      } else {
        // Original Logic: API Calls
        let response;
        if (mode === 'edit') {
          // For edit mode, update the existing segment
          const payload = {
            segment_name: formikForm.values.segmentName.trim(),
            description: formikForm.values.description.trim(),
            entity_id: currentModel?.entity_id,
            entity_type: currentModel?.entity_type,
            segment_id: segment?.segment_id,
            attribute_data: {
              ...attributeValues,
            },
          };
          response = await updateSegment(payload);
        } else {
          // For create and copy modes, create a new segment
          const payload = {
            segment_name: formikForm.values.segmentName.trim(),
            description: formikForm.values.description.trim(),
            entity_id: currentModel?.entity_id,
            entity_type: currentModel?.entity_type,
            attribute_data: {
              ...attributeValues,
            },
          };
          response = await createSegment(payload);
        }

        if (response?.data?.Message) {
          SwalToast({
            title: response?.data?.Message,
            icon: 'success',
          });
        }
        onSuccess();
        handleModalClose();
      }
    } finally {
      bypassCopyDirtyCheckRef.current = false;
      setLoading(false);
    }
  }, [
    formikForm,
    entityDetailsFormikForm,
    mode,
    segment,
    currentModel,
    onSuccess,
    handleModalClose,
    createSegment,
    updateSegment,
    segmentsData,
    attributeDetails,
    formDetails,
    isSegmentAttribute,
    selectedSegmentToCopy,
    excludedSegmentAttributeIds,
  ]);

  const handleConfirmOpen = useCallback(() => {
    bypassCopyDirtyCheckRef.current = true;
    closeConfirmModalCopySegment();
    handleSaveSegment();
  }, [closeConfirmModalCopySegment, handleSaveSegment]);

  // Reset selected segment to copy when modal opens in copy/replace/update mode
  useEffect(() => {
    // Reset the ref when the modal is closed
    if (!open) {
      initializationRef.current = false;
      return;
    }

    // If we've already initialized for this session, do nothing.
    // This breaks the infinite loop and allows the user to freely change the dropdown.
    if (initializationRef.current) return;

    if (mode === 'copy') {
      // Default to DEFAULT_SEGMENT_ID so users start with the model data as the copy source.
      // The state already initialises to DEFAULT_SEGMENT_ID, so just mark as done.
      setSelectedSegmentToCopy(DEFAULT_SEGMENT_ID);
      formikForm.setFieldValue('selectedSegmentToCopy', DEFAULT_SEGMENT_ID);
      initializationRef.current = true;
      return;
    }

    if (mode === 'replace') {
      const firstSegmentData = updatableSegments[0];
      if (!firstSegmentData) return;
      setSelectedSegmentToCopy(firstSegmentData?.segment_id);
      formikForm.setFieldValue(
        'selectedSegmentToCopy',
        firstSegmentData?.segment_id
      );
      initializationRef.current = true;
      return;
    }

    if (mode === 'update' && updatableSegments?.length > 0) {
      // Use updatableSegments[0] so the pre-selected value always matches the first
      // option visible in the dropdown (which excludes DEFAULT_SEGMENT_ID and
      // decommissioned segments — the old segmentsData.find could select a segment
      // that is decommissioned and therefore absent from the dropdown).
      const firstSegmentData = updatableSegments[0];

      // If segments haven't loaded yet, return early so we can try again when they do
      if (!firstSegmentData) return;

      setSelectedSegmentToCopy(firstSegmentData.segment_id);
      formikForm.setFieldValue(
        'selectedSegmentToCopy',
        firstSegmentData.segment_id
      );
      prefillAndFetchSegmentDetails(firstSegmentData);

      // Mark as initialized so this block never runs again until the modal is closed and reopened
      initializationRef.current = true;
    }
  }, [open, mode, updatableSegments, prefillAndFetchSegmentDetails]);

  // Auto-enter edit mode when modal opens (except for view mode)
  useEffect(() => {
    if (open && allAttributes?.length > 0 && mode !== 'view') {
      handleEditForm();
    }
  }, [open, allAttributes?.length, handleEditForm, mode]);

  // Memoized button label
  const buttonLabel = useMemo(() => {
    if (mode === 'view') return 'Close';
    if (loading) return 'Saving...';
    if (mode === 'edit') return 'Update';
    if (mode === 'update') return 'Update';
    return 'Create';
  }, [loading, mode]);

  const confirmMessage = useMemo(() => {
    if (matchedSegmentIds?.length === 0) return '';

    return `Segment (${matchedSegmentIds?.join(
      ', '
    )}) already have the same attribute values. Do you still want to create the segment?`;
  }, [matchedSegmentIds]);

  const dropdownLabel = useMemo(() => {
    if (mode === 'replace') return 'Select Segment to Replace';
    if (mode === 'update') return 'Select Segment to Update';
    return 'Select Segment to Copy';
  }, [mode]);

  const handleSegmentNameChange = useCallback(
    (e) => {
      formikForm.handleChange(e);
      const value = e?.target?.value;
      if (
        (mode === 'create' || mode === 'copy' || mode === 'update') &&
        b2AttributeId &&
        entityDetailsFormikForm
      ) {
        entityDetailsFormikForm?.setFieldValue(b2AttributeId, value, true);
      }
    },
    [mode, b2AttributeId, entityDetailsFormikForm, formikForm]
  );

  return (
    <Dialog
      onClose={handleModalClose}
      open={open}
      maxWidth="lg"
      fullWidth={true}
      aria-labelledby="create-edit-segment-modal"
    >
      <DialogTitle>
        <Box component="span" aria-live="polite" aria-atomic="true">
          {title}
          <Tooltip title={getInfoTitle()}>
            <IconButton
              size="small"
              sx={{
                display:
                  mode === 'create' ||
                  mode === 'copy' ||
                  mode === 'replace' ||
                  mode === 'update'
                    ? 'inline-flex'
                    : 'none',
              }}
            >
              <InfoOutlinedIcon width={18} height={18} />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>
      <IconButton
        color="primary"
        aria-label="Close dialog"
        onClick={handleModalClose}
        sx={{
          position: 'absolute',
          right: 16,
          top: 8,
        }}
      >
        <CloseIcon width={24} height={24} />
      </IconButton>
      <DialogContent dividers>
        <Grid container spacing={3}>
          {(mode === 'copy' || mode === 'replace' || mode === 'update') && (
            <Grid size={6}>
              <DropDown
                label={dropdownLabel}
                value={selectedSegmentToCopy}
                onChange={handleSegmentToCopyChange}
                onBlur={formikForm.handleBlur}
                name="selectedSegmentToCopy"
                fullWidth
                required
                disabled={mode === 'update' && updatableSegments.length === 0}
                error={
                  formikForm.touched.selectedSegmentToCopy &&
                  Boolean(formikForm.errors.selectedSegmentToCopy)
                }
                helperText={
                  mode === 'update' && updatableSegments.length === 0
                    ? 'No segments available to update'
                    : formikForm.touched.selectedSegmentToCopy &&
                      formikForm.errors.selectedSegmentToCopy
                }
              >
                {mode === 'copy' && (
                  <DropdownItem
                    key={DEFAULT_SEGMENT_ID}
                    value={DEFAULT_SEGMENT_ID}
                  >
                    {DEFAULT_SEGMENT_ID}
                  </DropdownItem>
                )}
                {updatableSegments?.map((segmentItem) => (
                  <DropdownItem
                    key={segmentItem?.segment_id}
                    value={segmentItem?.segment_id}
                  >
                    {segmentItem?.segment_id}
                  </DropdownItem>
                ))}
              </DropDown>
            </Grid>
          )}
          <Grid
            size={
              mode === 'copy' || mode === 'replace' || mode === 'update'
                ? 6
                : 12
            }
          >
            <TextField
              name="segmentName"
              label="Segment name"
              placeholder="Enter segment name"
              value={formikForm.values.segmentName}
              onChange={handleSegmentNameChange}
              onBlur={formikForm.handleBlur}
              error={
                formikForm.touched.segmentName &&
                Boolean(formikForm.errors.segmentName)
              }
              helperText={
                formikForm.touched.segmentName && formikForm.errors.segmentName
              }
              fullWidth
              required={mode !== 'view'}
              slotProps={{
                input: {
                  readOnly: mode === 'view',
                },

                inputLabel: { shrink: true },
              }}
            />
          </Grid>
          <Grid size={12}>
            <TextField
              name="description"
              label="Description"
              placeholder="Enter description (optional)"
              value={formikForm.values.description}
              onChange={formikForm.handleChange}
              onBlur={formikForm.handleBlur}
              error={
                formikForm.touched.description &&
                Boolean(formikForm.errors.description)
              }
              helperText={
                formikForm.touched.description && formikForm.errors.description
              }
              fullWidth
              multiline
              rows={3}
              slotProps={{
                input: {
                  readOnly: mode === 'view',
                },

                inputLabel: { shrink: true },
              }}
            />
          </Grid>
          {isSegmentAttribute && (
            <Grid size={12}>
              <TextField
                name="justification"
                label="Justification"
                placeholder="Enter justification"
                value={formikForm.values.justification}
                onChange={formikForm.handleChange}
                onBlur={formikForm.handleBlur}
                fullWidth
                multiline
                rows={3}
                error={
                  formikForm.touched.justification &&
                  Boolean(formikForm.errors.justification)
                }
                helperText={
                  formikForm.touched.justification &&
                  formikForm.errors.justification
                }
                required={isSegmentAttribute && mode !== 'view'}
                slotProps={{
                  input: {
                    readOnly: mode === 'view',
                  },

                  inputLabel: { shrink: true },
                }}
              />
            </Grid>
          )}
          <Grid size={12}>
            <Overview
              ref={overviewRef}
              formDetails={formDetails}
              attributeDetails={modalAttributeDetails}
              handleUpdateEntity={_handleUpdateEntity}
              usersList={allUserList}
              teamUsersList={teamUsersList}
              entityData={currentModel}
              entityDetailsFormikForm={entityDetailsFormikForm}
              validateFilteredFormikErrors={validateFilteredFormikErrors}
              isMultipleEditMode={mode !== 'view'}
              isUserAuthorizedForUpdateAttribute={
                mode !== 'view' && isUserAuthorizedForUpdateAttribute
              }
              deleteNote={deleteNote}
              editNote={editNote}
              handleOpenNoteModal={handleOpenNoteModal}
              handleSetAddNoteModalHeading={handleSetAddNoteModalHeading}
              handleSetSelectedEntityToAddInEditor={
                handleSetSelectedEntityToAddInEditor
              }
              getAttComments={getAttComments}
              allAttributeChats={allAttributeChats}
              syncAttributes={{}}
              isTableView={true}
              loading={loading}
              breadcrumbDetails={breadcrumbDetails}
              getOverrideAttributeValue={getOverrideAttributeValue}
              showErrorsOnSave={showValidation}
              setShowErrorsOnSave={setShowValidation}
              setFormSchema={setFormSchema}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        {mode === 'view' ? (
          <Button onClick={handleModalClose} variant="contained" title="Close">
            {buttonLabel}
          </Button>
        ) : (
          <>
            <Button
              onClick={handleModalClose}
              variant="outlined"
              title="Cancel"
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveSegment}
              variant="contained"
              title={mode === 'edit' || mode === 'update' ? 'Update' : 'Create'}
              disabled={
                loading || (mode === 'update' && updatableSegments.length === 0)
              }
            >
              {buttonLabel}
            </Button>
          </>
        )}
      </DialogActions>
      <ConfirmModal
        open={isConfirmModalOpen}
        handleClose={closeConfirmModalCopySegment}
        confirm={handleConfirmOpen}
        cancel={handleConfirmClose}
        alertLabelText={confirmMessage}
        confirmButtonLabel="Yes"
        cancelButtonLabel="No"
        resourceName="Create Copy Segment"
      />
    </Dialog>
  );
};

CreateEditSegmentModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  mode: PropTypes.oneOf(['create', 'edit', 'copy', 'view', 'replace']),
  title: PropTypes.string.isRequired,
  segment: PropTypes.oneOfType([PropTypes.object]),
  formDetails: PropTypes.oneOfType([PropTypes.object]).isRequired,
  attributeDetails: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.bool,
      PropTypes.object,
      PropTypes.array,
    ])
  ).isRequired,
  handleUpdateEntity: PropTypes.func.isRequired,
  allUserList: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  teamUsersList: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  currentModel: PropTypes.oneOfType([PropTypes.object]).isRequired,
  onSuccess: PropTypes.func.isRequired,
  deleteNote: PropTypes.func.isRequired,
  editNote: PropTypes.func.isRequired,
  handleSetAddNoteModalHeading: PropTypes.func.isRequired,
  handleSetSelectedEntityToAddInEditor: PropTypes.func.isRequired,
  getAttComments: PropTypes.func.isRequired,
  allAttributeChats: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.object)])
    .isRequired,
  breadcrumbDetails: PropTypes.oneOfType([PropTypes.object]).isRequired,
  isUserAuthorizedForUpdateAttribute: PropTypes.bool.isRequired,
  getOverrideAttributeValue: PropTypes.func,
  segmentsData: PropTypes.arrayOf(PropTypes.shape({})),
  allTemplateData: PropTypes.oneOfType([PropTypes.object]),
  isSegmentAttribute: PropTypes.bool,
};

export default CreateEditSegmentModal;
